import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebaseConfig';
import '../components/ScrimCalendar/scrimStyles.css';
import { setPersistence, browserLocalPersistence } from 'firebase/auth';
import { DateTime } from 'luxon';
import {
  doc,
  getDoc,
  getDocs,
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  where,
} from 'firebase/firestore';
import ScrimChip from '../components/ScrimCalendar/ScrimChip';
import ScrimEventModal from '../components/ScrimCalendar/ScrimEventModal';
import AgendaPanel from '../components/ScrimCalendar/AgendaPanel';
import { rateLimiter, formatTimeUntilReset } from '../utils/rateLimiter';
import { fetchActiveSeasonDoc } from '../utils/seasonApi';
import { getSeasonSelection, subscribeSeasonSelection } from '../utils/seasonSelection';
import {
  addDays,
  addWeeks,
  addMonths,
  subMonths,
  subWeeks,
  startOfWeek,
  endOfWeek,
} from 'date-fns';
import TitleSetter from '../components/TitleSetter';
import TopBar from '../components/TopBar';

const getDaysInMonth = (year, month) => {
  const date = new Date(year, month, 1);
  const days = [];
  while (date.getMonth() === month) {
    days.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }
  return days;
};

const toDate = (value) => {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  if (value instanceof Date) return value;
  return null;
};

const formatLocalDate = (date) =>
  date ? DateTime.fromJSDate(date).toFormat('yyyy-MM-dd') : '';

const formatDateInZone = (date, timeZone) =>
  date ? DateTime.fromJSDate(date).setZone(timeZone || 'UTC').toFormat('yyyy-MM-dd') : '';

const formatTimeInZone = (date, timeZone) =>
  date ? DateTime.fromJSDate(date).setZone(timeZone || 'UTC').toFormat('HH:mm') : '';

const zonedDateTimeToUTC = (dateStr, timeStr, timeZone) => {
  if (!dateStr) return null;
  const dt = DateTime.fromISO(`${dateStr}T${timeStr || '00:00'}`, { zone: timeZone || 'UTC' });
  return dt.isValid ? dt.toUTC().toJSDate() : null;
};

const buildLocalDateTime = (dateStr, timeStr) => {
  if (!dateStr) return null;
  const dt = DateTime.fromISO(`${dateStr}T${timeStr || '00:00'}`);
  return dt.isValid ? dt.toJSDate() : null;
};

const getScrimStart = (scrim) => {
  if (!scrim) return null;
  if (scrim.allDay) {
    return buildLocalDateTime(scrim.startDate, '00:00');
  }
  return toDate(scrim.startAt) || buildLocalDateTime(scrim.startDate, scrim.startTime || '00:00');
};

const getScrimEnd = (scrim) => {
  if (!scrim) return null;
  if (scrim.allDay) {
    return buildLocalDateTime(scrim.endDate, '23:59');
  }
  return (
    toDate(scrim.endAt) ||
    buildLocalDateTime(scrim.endDate, scrim.endTime || scrim.startTime || '00:00')
  );
};

const formatTimeRange = (start, end, allDay) => {
  if (allDay) return 'All day';
  if (!start || !end) return 'Time TBD';
  const formatTime = (date) =>
    new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  return `${formatTime(start)} - ${formatTime(end)}`;
};

const formatWeekTitle = (start, end) => {
  if (!start || !end) return '';
  const sameMonth = start.getMonth() === end.getMonth();
  const formatMonthDay = (date) =>
    new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(date);
  const formatMonth = (date) =>
    new Intl.DateTimeFormat(undefined, { month: 'short' }).format(date);
  const startLabel = formatMonthDay(start);
  const endLabel = sameMonth ? end.getDate() : formatMonthDay(end);
  const monthLabel = formatMonth(start);
  return `${monthLabel} ${startLabel.split(' ')[1]} - ${endLabel}`;
};

const generateSeriesId = () =>
  `series_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const getScrimViewRange = (scrim) => {
  if (!scrim) return { startDate: '', endDate: '' };
  if (scrim.allDay) {
    return { startDate: scrim.startDate, endDate: scrim.endDate };
  }
  const startAt = toDate(scrim.startAt);
  const endAt = toDate(scrim.endAt) || startAt;
  if (startAt) {
    return { startDate: formatLocalDate(startAt), endDate: formatLocalDate(endAt) };
  }
  return { startDate: scrim.startDate, endDate: scrim.endDate };
};

const normalizeScrimForEdit = (scrim) => {
  if (!scrim) return null;
  if (scrim.allDay) {
    return {
      ...scrim,
      eventType: scrim.eventType || 'scrim',
      seriesName: scrim.seriesName || scrim.title || '',
      timeZone: scrim.sourceTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    };
  }
  const startAt = toDate(scrim.startAt);
  const endAt = toDate(scrim.endAt);
  const tz = scrim.sourceTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  if (!startAt || !endAt) {
    return {
      ...scrim,
      eventType: scrim.eventType || 'scrim',
      seriesName: scrim.seriesName || scrim.title || '',
      timeZone: tz,
    };
  }
  return {
    ...scrim,
    eventType: scrim.eventType || 'scrim',
    seriesName: scrim.seriesName || scrim.title || '',
    startDate: formatDateInZone(startAt, tz),
    endDate: formatDateInZone(endAt, tz),
    startTime: formatTimeInZone(startAt, tz),
    endTime: formatTimeInZone(endAt, tz),
    timeZone: tz,
  };
};

const ScrimCalendar = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [activeScrim, setActiveScrim] = useState(null);
  const [teamLogo, setTeamLogo] = useState('');
  const [userProfile, setUserProfile] = useState(null);
  const [teamId, setTeamId] = useState(null);
  const [activeSeason, setActiveSeason] = useState(null);
  const [scrims, setScrims] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [now, setNow] = useState(new Date());
  const [viewMode, setViewMode] = useState('month');
  const [expandedDays, setExpandedDays] = useState({});
  const [expandedStacks, setExpandedStacks] = useState({});
  const [discordConfig, setDiscordConfig] = useState(null);
  const [discordGuildId, setDiscordGuildId] = useState('');
  const [discordChannelId, setDiscordChannelId] = useState('');
  const [discordChannels, setDiscordChannels] = useState([]);
  const [discordLoading, setDiscordLoading] = useState(false);
  const [discordError, setDiscordError] = useState('');
  const [discordSaveStatus, setDiscordSaveStatus] = useState('');
  const [discordTestStatus, setDiscordTestStatus] = useState('');
  const [discordExpanded, setDiscordExpanded] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const discordTestTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const fallbackSeasonId = String(new Date().getFullYear());
  const calendarCollection = () =>
    collection(
      db,
      teamId
        ? `teams/${teamId}/seasons/${activeSeason || fallbackSeasonId}/calendar`
        : `teams/${teamId}/calendar`
    );

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthDays = getDaysInMonth(year, month);
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 0 });
  const weekDays = Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));
  const env =
    typeof import.meta !== 'undefined' && import.meta.env
      ? import.meta.env
      : typeof process !== 'undefined'
      ? process.env || {}
      : {};
  const discordClientId =
    env.VITE_DISCORD_CLIENT_ID ||
    env.REACT_APP_DISCORD_CLIENT_ID ||
    '1453714479509409872';
  const functionsBaseUrl =
    env.VITE_FUNCTIONS_BASE_URL ||
    env.REACT_APP_FUNCTIONS_BASE_URL ||
    `https://us-central1-${db.app.options.projectId}.cloudfunctions.net`;

  useEffect(() => {
    setPersistence(auth, browserLocalPersistence).catch(console.error);
  }, []);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged((user) => {
      if (!user) navigate('/login');
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchUserProfile = async () => {
      const user = auth?.currentUser;
      if (!user) return;
      const userRef = doc(db, `users/${user.uid}`);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const data = userSnap.data();
        setUserProfile(data);
        if (data.teamId) {
          setTeamId(data.teamId);
          const teamRef = doc(db, `teams/${data.teamId}`);
          const teamSnap = await getDoc(teamRef);
          if (teamSnap.exists()) {
            const teamData = teamSnap.data();
            setTeamLogo(teamData.logo);
            const ownerUid = teamData.owner || teamData.ownerId || '';
            const currentUid = user.uid;
            setIsOwner(ownerUid ? ownerUid === currentUid : false);
            if (teamData.discord) {
              setDiscordConfig(teamData.discord);
              setDiscordGuildId(teamData.discord.guildId || '');
              setDiscordChannelId(teamData.discord.channelId || '');
            }
          }
        }
      }
    };
    fetchUserProfile();
  }, []);

  useEffect(() => {
    if (!teamId) return;
    let isMounted = true;
    const storedSeason = getSeasonSelection(teamId, { preferYear: true });
    if (storedSeason) {
      setActiveSeason(String(storedSeason));
    } else {
      fetchActiveSeasonDoc(teamId)
        .then(({ seasonId, year }) => {
          if (isMounted) {
            setActiveSeason(year ? String(year) : (seasonId ? String(seasonId) : null));
          }
        })
        .catch((err) => {
          console.error('Failed to load active season for calendar', err);
        });
    }

    const unsubscribe = subscribeSeasonSelection(teamId, (seasonId) => {
      if (isMounted) {
        setActiveSeason(seasonId ? String(seasonId) : null);
      }
    }, { preferYear: true });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [teamId]);

  useEffect(() => {
    if (!teamId) return;
    const scrimRef = calendarCollection();
    const unsubscribe = onSnapshot(scrimRef, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setScrims(data);
    });
    return () => unsubscribe();
  }, [teamId, activeSeason]);

  const generateRepeatScrims = (formData) => {
    const {
      startDate,
      endDate,
      startTime,
      endTime,
      allDay,
      repeat,
      customRepeat,
    } = formData;

    const scrims = [];
    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T00:00:00`);

    const formatDate = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    const getNextDate = (date, step = 1) => {
      if (repeat === 'daily') return addDays(date, step);
      if (repeat === 'weekly') return addWeeks(date, step);
      if (repeat === 'biweekly') return addWeeks(date, step * 2);
      if (repeat === 'monthly') return addMonths(date, step);
      return date;
    };

    let current = new Date(start);
    let count = 0;
    const customInterval = Math.max(1, parseInt(customRepeat.interval, 10) || 1);

    while (current <= end) {
      const dayOfWeek = current.toLocaleDateString('en-US', { weekday: 'short' });

      if (
        repeat === 'custom' &&
        customRepeat.unit === 'week' &&
        !customRepeat.days.includes(dayOfWeek)
      ) {
        current = addDays(current, 1);
        continue;
      }

      if (
        repeat === 'custom' &&
        customRepeat.ends === 'on' &&
        current > new Date(`${customRepeat.untilDate}T00:00:00`)
      ) {
        break;
      }

      if (
        repeat === 'custom' &&
        customRepeat.ends === 'count' &&
        count >= customRepeat.occurrences
      ) {
        break;
      }

      const { id, ...safeFormData } = formData;

      scrims.push({
        ...safeFormData,
        startDate: formatDate(current),
        endDate: formatDate(current),
        startTime,
        endTime,
        allDay,
      });

      count++;
      current =
        repeat === 'custom'
          ? addDays(current, customInterval)
          : getNextDate(current);
    }

    return scrims;
  };

  const fetchDiscordChannels = async (guildId) => {
    if (!guildId) return;
    setDiscordLoading(true);
    setDiscordError('');
    try {
      const response = await fetch(`${functionsBaseUrl}/discordChannels`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guildId }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || 'Failed to load channels.');
      }
      setDiscordChannels(data.channels || []);
    } catch (err) {
      console.error('Discord channel load error', err);
      setDiscordError('Failed to load Discord channels.');
      setDiscordChannels([]);
    } finally {
      setDiscordLoading(false);
    }
  };

  const handleDiscordConnect = () => {
    if (!discordClientId) {
      setDiscordError('Missing Discord client id.');
      return;
    }
    setDiscordError('');
    const redirectUri = `${window.location.origin}/my-calendar`;
    const state = teamId || '';
    const inviteUrl = `https://discord.com/oauth2/authorize?client_id=${discordClientId}&permissions=18432&scope=bot&response_type=code&redirect_uri=${encodeURIComponent(
      redirectUri
    )}&state=${encodeURIComponent(state)}`;
    window.location.href = inviteUrl;
  };

  const handleSaveDiscordConfig = async () => {
    if (!teamId || !discordGuildId || !discordChannelId) return;
    try {
      setDiscordError('');
      setDiscordSaveStatus('');
      const teamRef = doc(db, `teams/${teamId}`);
      const channel = discordChannels.find((c) => c.id === discordChannelId);
      const discordData = {
        guildId: discordGuildId,
        channelId: discordChannelId,
        channelName: channel?.name || '',
        updatedAt: serverTimestamp(),
      };
      await updateDoc(teamRef, { discord: discordData });
      setDiscordConfig(discordData);
      setDiscordSaveStatus(
        `Channel saved${discordData.channelName ? ` to #${discordData.channelName}` : ''}.`
      );
    } catch (err) {
      console.error('Failed to save Discord config', err);
      setDiscordError('Failed to save Discord config.');
    }
  };

  const sendDiscordTestMessage = async (channelId) => {
    if (!channelId) return;
    if (discordTestTimeoutRef.current) {
      clearTimeout(discordTestTimeoutRef.current);
      discordTestTimeoutRef.current = null;
    }
    setDiscordTestStatus('Sending test message...');
    try {
      setDiscordError('');
      const response = await fetch(`${functionsBaseUrl}/discordTestMessage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ channelId }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || 'Failed to send test message.');
      }
      const channel = discordChannels.find((c) => c.id === channelId);
      setDiscordTestStatus(
        `Test message sent${channel?.name ? ` to #${channel.name}` : ''}.`
      );
      discordTestTimeoutRef.current = setTimeout(() => {
        setDiscordTestStatus('');
        discordTestTimeoutRef.current = null;
      }, 2000);
    } catch (err) {
      console.error('Discord test message error', err);
      setDiscordTestStatus('Failed to send test message.');
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const guildId = params.get('guild_id');
    const error = params.get('error');
    const state = params.get('state');
    if (error) {
      setDiscordError('Discord connection was cancelled.');
    }
    if (guildId) {
      if (state && teamId && state !== teamId) {
        setDiscordError('Discord connection did not match this team.');
      } else {
        setDiscordGuildId(guildId);
        fetchDiscordChannels(guildId);
      }
    }
    if (error || guildId) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [teamId]);

  useEffect(() => {
    if (discordGuildId) {
      fetchDiscordChannels(discordGuildId);
    }
  }, [discordGuildId]);

  useEffect(() => {
    return () => {
      if (discordTestTimeoutRef.current) {
        clearTimeout(discordTestTimeoutRef.current);
      }
    };
  }, []);

  const channelUnchanged =
    Boolean(discordConfig?.channelId) && discordConfig.channelId === discordChannelId;
  const canSaveChannel =
    Boolean(discordChannelId) && !discordLoading && !channelUnchanged;
  const showTestDelivery = Boolean(discordConfig?.channelId) && channelUnchanged;
  const canSendTest =
    Boolean(discordConfig?.channelId) &&
    discordConfig.channelId === discordChannelId &&
    !discordLoading;
  const currentPeriodTitle =
    viewMode === 'week'
      ? formatWeekTitle(weekStart, weekEnd)
      : `${currentDate.toLocaleString('default', { month: 'long' })} ${currentDate.getFullYear()}`;

  const deleteSeriesScrims = async (calendarRef, seriesId, fromIndex = null) => {
    if (!seriesId) return;
    const snapshot = await getDocs(query(calendarRef, where('seriesId', '==', seriesId)));
    const deletions = snapshot.docs
      .filter((docSnap) => {
        if (typeof fromIndex !== 'number') return true;
        const idx = docSnap.data()?.seriesIndex;
        return typeof idx === 'number' && idx >= fromIndex;
      })
      .map((docSnap) => deleteDoc(docSnap.ref));
    await Promise.all(deletions);
  };

  const updateScrimDoc = async (scrimId, updates) => {
    if (!teamId || !scrimId) return;
    const calendarRef = calendarCollection();
    await updateDoc(doc(calendarRef, scrimId), {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  };

  const buildHistoryUpdate = (scrim, overrides = {}) => ({
    cancelled:
      overrides.cancelled ??
      scrim.history?.cancelled ??
      Boolean(scrim.cancelled),
    replaced:
      overrides.replaced ??
      scrim.history?.replaced ??
      Boolean(scrim.replaced),
    opponent: scrim.history?.opponent || scrim.opponent || '',
  });

  const handleCancelScrim = async (scrim, reason = '') => {
    if (!scrim?.id) return;
    await updateScrimDoc(scrim.id, {
      cancelled: true,
      cancelReason: reason,
      history: buildHistoryUpdate(scrim, { cancelled: true }),
    });
  };

  const handleUpdateScrim = async (scrim, updates) => {
    if (!scrim?.id) return;
    await updateScrimDoc(scrim.id, {
      ...updates,
    });
  };

  const handleReplaceScrim = async (scrim, payload) => {
    if (!scrim?.id) return;
    await updateScrimDoc(scrim.id, {
      cancelled: true,
      replaced: true,
      replacementOpponent: payload.replacementOpponent || '',
      actualContent: payload.actualContent || scrim.actualContent || '',
      replacementNotes: payload.replacementNotes || '',
      history: buildHistoryUpdate(scrim, { cancelled: true, replaced: true }),
    });
  };

  const handleRecoveryAction = async (scrim, actionLabel) => {
    if (!scrim?.id) return;
    await updateScrimDoc(scrim.id, {
      cancelled: true,
      replaced: false,
      actualContent: actionLabel,
      history: buildHistoryUpdate(scrim, { cancelled: true, replaced: false }),
    });
  };

  const stackScrimsByContinuity = (items) => {
    const sorted = [...items].sort((a, b) => (a._start || 0) - (b._start || 0));
    const groups = [];
    sorted.forEach((scrim) => {
      const last = groups[groups.length - 1];
      const sameTitle =
        last &&
        last.title &&
        scrim.title &&
        last.title.trim().toLowerCase() === scrim.title.trim().toLowerCase();
      const sameType = last && last.eventType === scrim.eventType;
      const stackable =
        !scrim.cancelled &&
        !scrim.replaced &&
        !last?.hasStatus &&
        sameTitle &&
        sameType &&
        last.end &&
        scrim._start &&
        Math.abs(scrim._start - last.end) <= 5 * 60 * 1000;

      if (stackable) {
        last.items.push(scrim);
        last.end = scrim._end || last.end;
        return;
      }

      groups.push({
        key: scrim.id,
        title: scrim.title || 'Untitled',
        eventType: scrim.eventType || 'scrim',
        start: scrim._start,
        end: scrim._end,
        items: [scrim],
        hasStatus: scrim.cancelled || scrim.replaced,
      });
    });
    return groups;
  };

  const buildDayGroups = (day) => {
    const dateStr = formatLocalDate(day);
    const dayScrims = scrims.filter((s) => {
      const range = getScrimViewRange(s);
      if (!range.startDate || !range.endDate) return false;
      return dateStr >= range.startDate && dateStr <= range.endDate;
    });
    const dayItems = dayScrims.map((scrim) => ({
      ...scrim,
      _start: getScrimStart(scrim),
      _end: getScrimEnd(scrim),
    }));
    const groups = stackScrimsByContinuity(dayItems);
    return {
      dateStr,
      groups,
      isToday: dateStr === formatLocalDate(now),
    };
  };

  const renderDayCell = (day, key) => {
    const { dateStr, groups, isToday } = buildDayGroups(day);
    const maxVisible = viewMode === 'week' ? 8 : 3;
    const isExpandedDay = Boolean(expandedDays[dateStr]);
    const visibleGroups = isExpandedDay ? groups : groups.slice(0, maxVisible);
    const hiddenCount = Math.max(0, groups.length - visibleGroups.length);

    return (
      <div key={key} className={`calendar-cell${isToday ? ' is-today' : ''}`}>
        <div className="day-number">{day.getDate()}</div>
        {isToday && <div className="calendar-today-badge">Today</div>}
        {visibleGroups.map((group) => {
          const stackKey = `${dateStr}-${group.key}`;
          const isStacked = group.items.length > 1;
          const isStackExpanded = Boolean(expandedStacks[stackKey]);
          if (isStacked && !isStackExpanded) {
            const stackTime = formatTimeRange(group.start, group.end);
            return (
              <div
                key={stackKey}
                className="scrim-chip scrim-stack"
                onClick={() =>
                  setExpandedStacks((prev) => ({
                    ...prev,
                    [stackKey]: true,
                  }))
                }
              >
                <div className="scrim-chip-title">
                  SCRIM - {group.title} x{group.items.length}
                </div>
                <div className="scrim-chip-time">{stackTime}</div>
              </div>
            );
          }

          return (
            <div key={stackKey}>
              {isStacked && (
                <button
                  type="button"
                  className="scrim-stack-header"
                  onClick={() =>
                    setExpandedStacks((prev) => ({
                      ...prev,
                      [stackKey]: false,
                    }))
                  }
                >
                  Collapse stack x{group.items.length}
                </button>
              )}
              {group.items.map((s) => {
                const start = s._start;
                const end = s._end;
                const isPast = end ? end < now : false;
                const isLive = start && end ? start <= now && end > now : false;
                const timeLabel = formatTimeRange(start, end, s.allDay);
                return (
                  <ScrimChip
                    key={s.id}
                    scrim={s}
                    timeLabel={timeLabel}
                    isLive={isLive}
                    isPast={isPast}
                    onClick={() => {
                      setActiveScrim(normalizeScrimForEdit(s));
                      setModalOpen(true);
                    }}
                  />
                );
              })}
            </div>
          );
        })}
        {hiddenCount > 0 && !isExpandedDay && (
          <button
            type="button"
            className="calendar-more"
            onClick={() =>
              setExpandedDays((prev) => ({ ...prev, [dateStr]: true }))
            }
          >
            +{hiddenCount} more
          </button>
        )}
        {isExpandedDay && groups.length > maxVisible && (
          <button
            type="button"
            className="calendar-more"
            onClick={() =>
              setExpandedDays((prev) => ({ ...prev, [dateStr]: false }))
            }
          >
            Show less
          </button>
        )}
      </div>
    );
  };

  const visibleDays = viewMode === 'week' ? weekDays : monthDays;
  const visiblePeriodHasEvents = visibleDays.some((day) => buildDayGroups(day).groups.length > 0);

  return (
    <>
      <TitleSetter title="MyCalendar" />
    <div className="calendar-page">
      <TopBar
        userProfile={userProfile}
        teamLogo={teamLogo}
        setUserProfile={setUserProfile}
        setTeamLogo={setTeamLogo}
      />
      <header className="calendar-page-header">
        <div>
          <p className="calendar-eyebrow">Team Scheduling</p>
          <h1>My Calendar</h1>
          <p>Team schedule, scrims, reviews & events</p>
        </div>
        <div className="calendar-page-actions">
          <div className="calendar-period-nav" aria-label="Calendar period navigation">
            <button
              type="button"
              aria-label={viewMode === 'week' ? 'Previous week' : 'Previous month'}
              onClick={() =>
                setCurrentDate((prev) =>
                  viewMode === 'week' ? subWeeks(prev, 1) : subMonths(prev, 1)
                )
              }
            >
              {'<'}
            </button>
            <span>{currentPeriodTitle}</span>
            <button
              type="button"
              aria-label={viewMode === 'week' ? 'Next week' : 'Next month'}
              onClick={() =>
                setCurrentDate((prev) =>
                  viewMode === 'week' ? addWeeks(prev, 1) : addMonths(prev, 1)
                )
              }
            >
              {'>'}
            </button>
          </div>
          <div className="calendar-view-toggle" aria-label="Calendar view">
            <button
              type="button"
              className={viewMode === 'week' ? 'is-active' : ''}
              onClick={() => setViewMode('week')}
            >
              Week
            </button>
            <button
              type="button"
              className={viewMode === 'month' ? 'is-active' : ''}
              onClick={() => setViewMode('month')}
            >
              Month
            </button>
          </div>
          <button
            type="button"
            className="calendar-secondary-action"
            onClick={() => setCurrentDate(new Date())}
          >
            Today
          </button>
          <button
            type="button"
            className="calendar-primary-action"
            onClick={() => {
              setActiveScrim(null);
              setModalOpen(true);
            }}
          >
            + New Scrim
          </button>
        </div>
      </header>
      <div className="calendar-layout">
      <div className="calendar-side">
        <div className="calendar-insights">
        <AgendaPanel
          scrims={scrims}
          now={now}
          onSelectScrim={(scrim) => {
            setActiveScrim(normalizeScrimForEdit(scrim));
            setModalOpen(true);
          }}
          onCancelScrim={handleCancelScrim}
          onUpdateScrim={handleUpdateScrim}
          onReplaceScrim={handleReplaceScrim}
          onRecoveryAction={handleRecoveryAction}
        />
        </div>

        {isOwner ? (
          <div className={`discord-panel${discordConfig ? ' is-connected' : ''}${discordExpanded ? ' is-expanded' : ''}`}>
            <div className="discord-panel-header">
              <div className="discord-title">
                <img
                  src="/global%20logos/discord.png"
                  alt="Discord"
                  className="discord-logo"
                />
                <div>
                  <h3>Discord</h3>
                  <p>{discordConfig ? `Connected to #${discordConfig.channelName || discordChannelId}` : 'Scrim reminder integration'}</p>
                </div>
              </div>
              <div className="discord-header-actions">
                <span
                  className={`discord-status-badge ${
                    discordConfig ? 'is-connected' : 'is-disconnected'
                  }`}
                >
                  {discordConfig ? 'Connected' : 'Not connected'}
                </span>
                <button
                  className="discord-connect-btn primary"
                  onClick={handleDiscordConnect}
                  disabled={!teamId}
                >
                  {discordConfig ? 'Reconnect' : 'Connect'}
                </button>
                <button
                  type="button"
                  className="discord-collapse-toggle"
                  aria-expanded={discordExpanded}
                  onClick={() => setDiscordExpanded((prev) => !prev)}
                >
                  {discordExpanded ? 'Hide' : 'Details'}
                </button>
              </div>
            </div>
            {discordExpanded && discordGuildId && (
              <>
                <div className="discord-section">
                  <label>Notification channel</label>
                  <div className="discord-channel-row">
                    <select
                      value={discordChannelId}
                      onChange={(e) => {
                        setDiscordChannelId(e.target.value);
                        setDiscordSaveStatus('');
                        setDiscordTestStatus('');
                        if (discordTestTimeoutRef.current) {
                          clearTimeout(discordTestTimeoutRef.current);
                          discordTestTimeoutRef.current = null;
                        }
                      }}
                      disabled={discordLoading}
                    >
                      <option value="">Choose a channel</option>
                      {discordChannels.map((channel) => {
                        const labelPrefix = channel.type === 2 ? 'vc' : '#';
                        return (
                          <option key={channel.id} value={channel.id}>
                            {labelPrefix} {channel.name}
                          </option>
                        );
                      })}
                    </select>
                    <button
                      className="discord-save-btn secondary"
                      onClick={handleSaveDiscordConfig}
                      disabled={!canSaveChannel}
                    >
                      Save
                    </button>
                  </div>
                  <div className="discord-hint">
                    Messages will be sent to this channel.
                  </div>
                  {discordConfig && (
                    <div className="discord-status">
                      Connected to #{discordConfig.channelName || discordChannelId}
                    </div>
                  )}
                  {showTestDelivery && (
                    <div className="discord-test-row">
                      <span>Want to check it? -</span>
                      <button
                        type="button"
                        className="discord-inline-link"
                        onClick={() => sendDiscordTestMessage(discordChannelId)}
                        disabled={!canSendTest}
                      >
                        Send test
                      </button>
                    </div>
                  )}
                  {discordSaveStatus && (
                    <div className="discord-status">{discordSaveStatus}</div>
                  )}
                  {discordTestStatus && (
                    <div className="discord-status">{discordTestStatus}</div>
                  )}
                </div>
              </>
            )}
            {discordError && <div className="discord-error">{discordError}</div>}
          </div>
        ) : (
          <div className="discord-panel is-locked">
            <div className="discord-panel-header">
              <div className="discord-title">
                <img
                  src="/global%20logos/discord.png"
                  alt="Discord"
                  className="discord-logo"
                />
                <div>
                  <h3>Discord</h3>
                  <p>Only the team owner can configure Discord notifications.</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="calendar-divider" aria-hidden="true"></div>

      <div className="calendar-wrapper">
        <div className="calendar-grid">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, i) => (
            <div key={i} className="calendar-day-label">{d.toUpperCase()}</div>
          ))}

          {viewMode === 'month' &&
            Array(monthDays[0].getDay()).fill(null).map((_, i) => (
              <div key={`empty-${i}`} className="calendar-cell empty"></div>
            ))}

          {viewMode === 'month' &&
            monthDays.map((day, idx) => renderDayCell(day, idx))}

          {viewMode === 'week' &&
            weekDays.map((day, idx) => renderDayCell(day, `week-${idx}`))}
        </div>
        {!visiblePeriodHasEvents && (
          <div className="calendar-empty-period">
            <h3>No scheduled events this {viewMode === 'week' ? 'week' : 'month'}.</h3>
            <p>Plan the next scrim, review, or team session.</p>
            <button
              type="button"
              onClick={() => {
                setActiveScrim(null);
                setModalOpen(true);
              }}
            >
              + New Scrim
            </button>
          </div>
        )}
      </div>

      </div>

      <ScrimEventModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        initialData={activeScrim}
        existingScrims={scrims}
        activeScrimId={activeScrim?.id || null}
        onSave={async (formData) => {
          if (!teamId || !userProfile) return;
          
          // Check rate limit
          const check = rateLimiter.checkLimit('CREATE_SCRIM_EVENT');
          if (!check.allowed) {
            const timeUntilReset = formatTimeUntilReset(rateLimiter.getTimeUntilReset('CREATE_SCRIM_EVENT'));
            alert(`Rate limit exceeded. Please try again in ${timeUntilReset}.`);
            return;
          }

          if (!rateLimiter.record('CREATE_SCRIM_EVENT')) {
            const timeUntilReset = formatTimeUntilReset(rateLimiter.getTimeUntilReset('CREATE_SCRIM_EVENT'));
            alert(`Rate limit exceeded. Please try again in ${timeUntilReset}.`);
            return;
          }
          
          const calendarRef = calendarCollection();
          const now = serverTimestamp();
          const creatorTimeZone =
            Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
          const eventTimeZone = formData.timeZone || creatorTimeZone;

          const historySnapshot = {
            cancelled: formData.history?.cancelled ?? Boolean(formData.cancelled),
            replaced: formData.history?.replaced ?? Boolean(formData.replaced),
            opponent: formData.history?.opponent || formData.opponent || '',
          };

          const baseData = {
            title: formData.title || '',
            eventType: formData.eventType || 'scrim',
            startDate: formData.startDate,
            endDate: formData.endDate,
            startTime: formData.startTime || '',
            endTime: formData.endTime || '',
            allDay: Boolean(formData.allDay),
            repeat: formData.repeat || 'none',
            timeZone: eventTimeZone,
            note: formData.note || '',
            history: historySnapshot,
            updatedAt: now,
            createdBy: {
              uid: userProfile.uid || auth?.currentUser.uid,
              ign: userProfile.ign || 'Unknown',
            },
          };

          const buildEventTimes = (data) => {
            const timeZone = data.timeZone || eventTimeZone;
            if (data.allDay) {
              return { ...data, sourceTimeZone: timeZone };
            }
            const startAt = zonedDateTimeToUTC(
              data.startDate,
              data.startTime || '00:00',
              timeZone
            );
            const endAt = zonedDateTimeToUTC(
              data.endDate,
              data.endTime || data.startTime || '00:00',
              timeZone
            );
            return { ...data, startAt, endAt, sourceTimeZone: timeZone };
          };

          const baseWithTimes = buildEventTimes(baseData);
          const isSeries = Boolean(activeScrim?.seriesId);
          const seriesIndex =
            typeof activeScrim?.seriesIndex === 'number' ? activeScrim.seriesIndex : null;
          const scope = formData.editScope || (isSeries ? 'series' : 'single');
          const nextSeriesId = generateSeriesId();

          const deleteSeriesByScope = async (targetScope) => {
            if (!isSeries || !activeScrim?.seriesId) return;
            if (targetScope === 'following' && typeof seriesIndex === 'number') {
              await deleteSeriesScrims(calendarRef, activeScrim.seriesId, seriesIndex);
              return;
            }
            await deleteSeriesScrims(calendarRef, activeScrim.seriesId);
          };

          const removeSingleDoc = async () => {
            if (!activeScrim?.id) return;
            await deleteDoc(doc(calendarRef, activeScrim.id));
          };

          if (formData.skip && activeScrim?.id) {
            await removeSingleDoc();
            setScrims((prev) => prev.filter((s) => s.id !== activeScrim.id));
            setModalOpen(false);
            return;
          }

          if (formData.delete && activeScrim?.id) {
            if (isSeries) {
              if (scope === 'single') {
                await removeSingleDoc();
              } else {
                await deleteSeriesByScope(scope);
              }
            } else {
              await removeSingleDoc();
            }
            setScrims((prev) => prev.filter((s) => s.id !== activeScrim.id));
            setModalOpen(false);
            return;
          }

          if (formData.repeat === 'none') {
            if (isSeries) {
              if (scope === 'single') {
                await updateDoc(doc(calendarRef, activeScrim.id), {
                  ...baseWithTimes,
                  repeat: 'none',
                  seriesId: null,
                  seriesIndex: null,
                });
              } else {
                await deleteSeriesByScope(scope);
                await addDoc(calendarRef, baseWithTimes);
              }
            } else if (activeScrim?.id) {
              await updateDoc(doc(calendarRef, activeScrim.id), baseWithTimes);
            } else {
              await addDoc(calendarRef, baseWithTimes);
            }
          } else {
            if (isSeries && scope === 'single') {
              await updateDoc(doc(calendarRef, activeScrim.id), {
                ...baseWithTimes,
                repeat: 'none',
                seriesId: null,
                seriesIndex: null,
              });
            } else {
              if (isSeries) {
                await deleteSeriesByScope(scope);
              } else if (activeScrim?.id) {
                await removeSingleDoc();
              }
              const repeatScrims = generateRepeatScrims(formData).map((scrim, index) =>
                buildEventTimes({
                  ...scrim,
                  createdBy: baseData.createdBy,
                  updatedAt: now,
                  seriesId: nextSeriesId,
                  seriesIndex: index + 1,
                })
              );
              for (const s of repeatScrims) {
                await addDoc(calendarRef, s);
              }
            }
          }

          setModalOpen(false);
        }}
      />
    </div>
    </>
  );
};

export default ScrimCalendar;
